import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, PauseCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AccountStatusInfo } from "@/hooks/useAccountStatus";

/**
 * Non-blocking banner shown at the top of the dashboard shell when the
 * current user has a non-active account status. Mirrors server-side rules.
 */
export function AccountStatusBanner({ info }: { info: AccountStatusInfo | null | undefined }) {
  const { t } = useTranslation();
  if (!info || info.status === "active") return null;

  const reason = info.reason?.trim();

  // Tone -> token mapping (semantic only, matches sapphire premium theme)
  const toneClass: Record<string, string> = {
    info: "border-l-4 border-primary/60 bg-primary/5 text-foreground",
    warn: "border-l-4 border-amber-500/60 bg-amber-500/5 text-foreground",
    danger: "border-l-4 border-destructive/70 bg-destructive/5 text-foreground",
  };

  let tone: keyof typeof toneClass = "info";
  let Icon = Info;
  let title = "";
  let body = "";
  let action: { to: string; label: string } | null = null;

  switch (info.status) {
    case "on_hold":
      tone = "warn";
      Icon = PauseCircle;
      title = t("account.statusBanner.on_hold.title", "Your account is on hold");
      body = t("account.statusBanner.on_hold.body", "Some actions may be temporarily limited.");
      break;
    case "deactivated":
      tone = "info";
      Icon = PauseCircle;
      title = t("account.statusBanner.deactivated.title", "Account deactivated");
      body = t("account.statusBanner.deactivated.body", "Reactivate it any time from your account settings.");
      action = { to: "/account/danger-zone", label: t("account.statusBanner.deactivated.action", "Reactivate") };
      break;
    case "pending_deletion": {
      tone = "danger";
      Icon = Trash2;
      const days = info.queueScheduledFor
        ? Math.max(0, Math.ceil((new Date(info.queueScheduledFor).getTime() - Date.now()) / 86400000))
        : null;
      title = t("account.statusBanner.pending_deletion.title", "Account scheduled for deletion");
      body =
        days !== null
          ? t("account.statusBanner.pending_deletion.bodyDays", "Becomes permanent in {{count}} day(s). Cancel any time.", { count: days })
          : t("account.statusBanner.pending_deletion.body", "Cancel before it becomes permanent.");
      action = { to: "/account/danger-zone", label: t("account.statusBanner.pending_deletion.action", "Cancel deletion") };
      break;
    }
    case "suspended":
    case "anonymized":
      tone = "danger";
      Icon = AlertTriangle;
      title = t("account.statusBanner.suspended.title", "Account access restricted");
      body = t("account.statusBanner.suspended.body", "Contact support if you believe this is a mistake.");
      break;
  }

  return (
    <div className={`w-full px-4 py-2.5 text-sm flex items-center gap-3 ${toneClass[tone]}`}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="font-medium leading-tight">{title}</div>
        <div className="text-muted-foreground text-xs leading-snug">
          {body}
          {reason ? <span className="ml-1 italic">— {reason}</span> : null}
        </div>
      </div>
      {action ? (
        <Button asChild size="sm" variant="outline" className="shrink-0">
          <Link to={action.to}>{action.label}</Link>
        </Button>
      ) : null}
    </div>
  );
}
